/**
 * Theme Zustand Store
 * Tracks the currently selected theme ID for reactive UI updates.
 * Actual theme application is handled by theme-engine.ts via DOM manipulation.
 */

import { create } from "zustand";
import { getPersistedThemeId } from "../lib/theme-engine";

const THEMED_LOGO_KEY = "provenleap-themed-logo";
const DUOTONE_ICONS_KEY = "provenleap-duotone-icons";

/** Toggle the duotone-icons class on the authenticated container. */
function applyDuotoneClass(enabled: boolean) {
  const el = document.getElementById("authenticated-root");
  if (!el) return;
  el.classList.toggle("duotone-icons", enabled);
}

/** Apply or remove logo CSS custom properties on the authenticated container. */
function applyLogoVars(themed: boolean) {
  const el = document.getElementById("authenticated-root");
  if (!el) return;
  if (themed) {
    el.style.setProperty("--logo-proven", "hsl(var(--primary))");
    el.style.setProperty("--logo-leap", "hsl(var(--foreground))");
  } else {
    el.style.removeProperty("--logo-proven");
    el.style.removeProperty("--logo-leap");
  }
}

interface ThemeStore {
  currentThemeId: string;
  setThemeId: (id: string) => void;
  themedLogo: boolean;
  setThemedLogo: (v: boolean) => void;
  duotoneIcons: boolean;
  setDuotoneIcons: (v: boolean) => void;
}

const initialThemedLogo = localStorage.getItem(THEMED_LOGO_KEY) === "true";
const initialDuotoneIcons = localStorage.getItem(DUOTONE_ICONS_KEY) !== "false";

export const useThemeStore = create<ThemeStore>((set) => ({
  currentThemeId: getPersistedThemeId(),
  setThemeId: (id) => set({ currentThemeId: id }),
  themedLogo: initialThemedLogo,
  setThemedLogo: (v) => {
    localStorage.setItem(THEMED_LOGO_KEY, String(v));
    applyLogoVars(v);
    set({ themedLogo: v });
  },
  duotoneIcons: initialDuotoneIcons,
  setDuotoneIcons: (v) => {
    localStorage.setItem(DUOTONE_ICONS_KEY, String(v));
    applyDuotoneClass(v);
    set({ duotoneIcons: v });
  },
}));
